import React, { useEffect } from "react";
import { Flex, Box, useColorModeValue, Icon, Text } from "@chakra-ui/react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { FaUser, FaCheckCircle, FaCamera, FaThumbsUp } from "react-icons/fa";

interface TabItem {
  label: string;
  path: string;
  icon: React.ElementType;
  additionalPaths?: string[];
}

const tabs: TabItem[] = [
  { label: "Dados", path: "consignado/dados-pessoais", icon: FaUser },
  { label: "Confirmação", path: "consignado/confirmacao", icon: FaThumbsUp },
  { label: "Selfie", path: "consignado/selfie", icon: FaCamera },
  {
    label: "Aprovação",
    path: "consignado/upload-documentos",
    icon: FaCheckCircle,
    additionalPaths: ["consignado/documento-digital", "consignado/outros-documentos"],
  },
];

export default function TabNavigatorConsignado() {
  const navigate = useNavigate();
  const location = useLocation();
  const { shorthash } = useParams<{ shorthash: string }>();
  const maxTabIndexStorageKey = shorthash
    ? `consignado:max-tab-index:${shorthash}`
    : null;

  const getCurrentIndex = () => {
    const index = tabs.findIndex((tab) => {
      if (location.pathname.includes(tab.path)) {
        return true;
      }

      if (tab.additionalPaths) {
        return tab.additionalPaths.some((additionalPath) =>
          location.pathname.includes(additionalPath)
        );
      }
      return false;
    });
    return index !== -1 ? index : 0;
  };

  const currentIndex = getCurrentIndex();

  useEffect(() => {
    if (!shorthash || !maxTabIndexStorageKey) {
      return;
    }

    const savedMaxIndex = Number(sessionStorage.getItem(maxTabIndexStorageKey) ?? 0);
    const normalizedSavedMaxIndex = Number.isNaN(savedMaxIndex)
      ? 0
      : Math.max(0, Math.min(savedMaxIndex, tabs.length - 1));

    if (currentIndex < normalizedSavedMaxIndex) {
      navigate(`/${shorthash}/${tabs[normalizedSavedMaxIndex].path}`, {
        replace: true,
        state: location.state,
      });
      return;
    }

    if (currentIndex > normalizedSavedMaxIndex) {
      sessionStorage.setItem(maxTabIndexStorageKey, String(currentIndex));
    }
  }, [currentIndex, location.state, maxTabIndexStorageKey, navigate, shorthash]);

  const handleTabClick = (index: number, path: string) => {
    if (index <= currentIndex && shorthash) {
      navigate(`/${shorthash}/${path}`);
    }
  };

  const tabColor = useColorModeValue("gray.600", "gray.300");
  const bgColor = useColorModeValue("white", "gray.800");

  return (
    <Flex
      as="nav"
      bg={bgColor}
      borderTopWidth="1px"
      boxShadow="0 -2px 10px rgba(0, 0, 0, 0.2)"
      py={3}
      px={2}
      justify="space-between"
      align="center"
      width="100%"
      maxWidth="100%"
      overflowX="auto"
      gap={2}
      css={{
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        '-ms-overflow-style': 'none'
      }}
    >
      {tabs.map((tab, index) => {
        const isActive = index === currentIndex;
        const isCompleted = index < currentIndex;
        const isDisabled = index > currentIndex;

        return (
          <Box
            key={tab.path}
            as="button"
            onClick={() => handleTabClick(index, tab.path)}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={2}
            px={1}
            flex="1"
            minWidth="auto"
            height="100%"
            color={isActive ? "green.500" : isCompleted ? "gray.600" : tabColor}
            fontWeight={isActive ? "bold" : "normal"}
            opacity={isDisabled ? 0.5 : 1}
            transition="all 0.2s ease"
            _hover={!isDisabled ? { color: "green.500" } : {}}
            disabled={isDisabled || isCompleted}
            cursor={isDisabled ? "not-allowed" : "pointer"}
          >
            <Icon 
              as={tab.icon} 
              boxSize={5} 
              mb={1.5}
              color={isActive ? "green.500" : isCompleted ? "gray.600" : tabColor}
            />
            <Text 
              fontSize="sm" 
              noOfLines={1} 
              textAlign="center"
              lineHeight="1.2"
              maxW="100%"
              px={1}
            >
              {tab.label}
            </Text>
          </Box>
        );
      })}
    </Flex>
  );
}